/**
 * Vista de Presupuestos y Metas de FinVoz (FR-BUDGET-1..5, spec/01-requirements.md §7).
 *
 * Muestra:
 *  - Presupuestos de gasto con consumo derivado del libro (INV-4, FR-BUDGET-2)
 *  - Avisos por umbral configurable y limite superado (FR-BUDGET-3)
 *  - Metas de ahorro con progreso derivado del saldo de su cuenta (FR-BUDGET-4, FR-BUDGET-5)
 *  - Modales para crear nuevos presupuestos y metas
 */

import type { AppState, Budget, Goal } from "../types.js";
import { formatMinorUnits } from "../format.js";
import { escapeHtml } from "../sanitize.js";
import { accountBalance } from "../../core/ledger.js";

export function renderBudgets(state: AppState): string {
  const currency = state.currency || "PEN";
  const scale = 2;
  const rawEntries = state.entries.map((e) => e.entry);
  const expenseAccounts = state.accounts.filter((a) => a.type === "expense" && !a.archived);
  const assetAccounts = state.accounts.filter((a) => a.type === "asset" && !a.archived);

  return `
    <div class="budgets-view" data-testid="budgets-view">
      <!-- SECCION PRESUPUESTOS (FR-BUDGET-1..3) -->
      <div class="card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Presupuestos de Gasto</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              El consumo se deriva exclusivamente del libro (INV-4, FR-BUDGET-2)
            </div>
          </div>
          <button class="btn-primary" data-testid="new-budget-btn" data-action="open-new-budget-modal">
            ➕ Nuevo Presupuesto
          </button>
        </div>

        ${state.budgets.length === 0
          ? `<div style="text-align: center; color: var(--text-muted); padding: 24px 0;">No hay presupuestos definidos. Crea uno para controlar tus gastos.</div>`
          : `
            <div class="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Cuenta de Gasto</th>
                    <th>Periodo</th>
                    <th>Consumo / Límite</th>
                    <th>Restante</th>
                    <th>Estado</th>
                    <th style="text-align: right;">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  ${state.budgets.map((b) => {
                    const acc = state.accounts.find((a) => a.accountId === b.accountId);
                    const consumed = accountBalance(b.accountId, rawEntries, currency, scale).amountMinor;
                    const remaining = b.limitMinor - consumed;
                    const pct = b.limitMinor > 0n ? Number((consumed * 100n) / b.limitMinor) : 0;
                    const isExceeded = consumed > b.limitMinor;
                    const isWarning = pct >= b.thresholdPct;

                    return `
                      <tr data-testid="budget-row-${escapeHtml(b.budgetId)}">
                        <td style="font-weight: 500;">${escapeHtml(acc?.name ?? b.accountId)}</td>
                        <td style="text-transform: capitalize;">${b.period === 'monthly' ? 'Mensual' : 'Anual'}</td>
                        <td style="font-family: var(--font-mono);">
                          ${formatMinorUnits(consumed, currency, scale)} / ${formatMinorUnits(b.limitMinor, currency, scale)}
                          <div class="progress-bar-bg" style="width: 140px;">
                            <div class="progress-bar-fill" style="width: ${Math.min(pct, 100)}%; background-color: ${isExceeded ? 'var(--danger)' : isWarning ? 'var(--warning)' : 'var(--accent)'};"></div>
                          </div>
                        </td>
                        <td style="font-family: var(--font-mono); color: ${remaining < 0n ? 'var(--danger)' : 'var(--success)'}; font-weight: 600;">
                          ${formatMinorUnits(remaining, currency, scale)}
                        </td>
                        <td>
                          ${isExceeded
                            ? `<span class="badge" style="background-color: var(--danger-bg); color: var(--danger);">¡Superado!</span>`
                            : isWarning
                            ? `<span class="badge" style="background-color: var(--warning-bg); color: var(--warning);">Umbral ${b.thresholdPct}% superado</span>`
                            : `<span class="badge" style="background-color: var(--success-bg); color: var(--success);">Dentro de límite</span>`
                          }
                        </td>
                        <td style="text-align: right;">
                          <button class="btn-secondary" style="font-size: 0.75rem; padding: 2px 8px; color: var(--danger);" data-action="delete-budget" data-budget-id="${escapeHtml(b.budgetId)}">
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    `;
                  }).join("")}
                </tbody>
              </table>
            </div>
          `}
      </div>

      <!-- SECCION METAS DE AHORRO (FR-BUDGET-4, FR-BUDGET-5) -->
      <div class="card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Metas de Ahorro</h2>
            <div style="font-size: 0.85rem; color: var(--text-secondary);">
              El progreso se deriva del saldo real de la cuenta asociada (INV-4, FR-BUDGET-5)
            </div>
          </div>
          <button class="btn-primary" data-testid="new-goal-btn" data-action="open-new-goal-modal">
            🎯 Nueva Meta
          </button>
        </div>

        ${state.goals.length === 0
          ? `<div style="text-align: center; color: var(--text-muted); padding: 24px 0;">No hay metas de ahorro activas. Define una para proyectar tu progreso.</div>`
          : `
            <div class="grid-2">
              ${state.goals.map((g) => {
                const acc = state.accounts.find((a) => a.accountId === g.accountId);
                const currentBalance = accountBalance(g.accountId, rawEntries, currency, scale).amountMinor;
                const remaining = g.targetMinor > currentBalance ? g.targetMinor - currentBalance : 0n;
                const pct = g.targetMinor > 0n ? Number((currentBalance * 100n) / g.targetMinor) : 0;

                // Calcular ritmo mensual necesario
                const today = new Date();
                const targetDate = new Date(g.targetDate);
                const diffMonths = Math.max(1, (targetDate.getFullYear() - today.getFullYear()) * 12 + (targetDate.getMonth() - today.getMonth()));
                const monthlyNeeded = remaining > 0n ? remaining / BigInt(diffMonths) : 0n;

                return `
                  <div class="card" style="margin-bottom: 0; background-color: var(--bg-tertiary);" data-testid="goal-card-${escapeHtml(g.goalId)}">
                    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
                      <div>
                        <h3 style="font-size: 1.05rem; font-weight: 600;">${escapeHtml(g.name)}</h3>
                        <div style="font-size: 0.8rem; color: var(--text-secondary);">Cuenta: ${escapeHtml(acc?.name ?? g.accountId)}</div>
                      </div>
                      <span class="badge" style="background-color: var(--success-bg); color: var(--success);">
                        ${pct}% completado
                      </span>
                    </div>

                    <div style="font-family: var(--font-mono); font-size: 1.25rem; font-weight: 700; margin: 8px 0;">
                      ${formatMinorUnits(currentBalance, currency, scale)} / ${formatMinorUnits(g.targetMinor, currency, scale)}
                    </div>

                    <div class="progress-bar-bg">
                      <div class="progress-bar-fill" style="width: ${Math.min(pct, 100)}%; background-color: var(--success);"></div>
                    </div>

                    <div style="display: flex; justify-content: space-between; font-size: 0.8rem; color: var(--text-secondary); margin-top: 8px;">
                      <span>Faltan: ${formatMinorUnits(remaining, currency, scale)}</span>
                      <span>Fecha: ${escapeHtml(g.targetDate)}</span>
                    </div>

                    <div style="font-size: 0.8rem; color: var(--accent); margin-top: 6px;">
                      Ritmo sugerido: ~${formatMinorUnits(monthlyNeeded, currency, scale)} / mes
                    </div>
                  </div>
                `;
              }).join("")}
            </div>
          `}
      </div>

      <!-- Modal Nuevo Presupuesto -->
      <div id="new-budget-modal" class="modal-overlay" style="display: none;">
        <div class="modal-content">
          <div class="card-header">
            <h3 class="card-title">Definir Presupuesto de Gasto</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-new-budget-modal">&times;</button>
          </div>
          <form id="new-budget-form" data-form="new-budget">
            <div style="margin-bottom: 12px;">
              <label for="bg-account" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta de Gasto:</label>
              <select id="bg-account" data-testid="bg-account" required style="width: 100%;">
                ${expenseAccounts.map((a) => `<option value="${escapeHtml(a.accountId)}">${escapeHtml(a.name)}</option>`).join("")}
              </select>
            </div>
            <div style="margin-bottom: 12px;">
              <label for="bg-limit" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Límite de Gasto (${currency === "PEN" ? "S/" : currency === "USD" ? "$" : "€"}):</label>
              <input type="number" step="0.01" min="0.01" id="bg-limit" data-testid="bg-limit" required placeholder="Ej: 300.00" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="bg-threshold" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Umbral de Aviso (%):</label>
              <input type="number" min="1" max="100" id="bg-threshold" data-testid="bg-threshold" value="80" required style="width: 100%;" />
            </div>
            <div style="margin-bottom: 16px;">
              <label for="bg-period" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Periodo:</label>
              <select id="bg-period" data-testid="bg-period" required style="width: 100%;">
                <option value="monthly">Mensual</option>
                <option value="annual">Anual</option>
              </select>
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 8px;">
              <button type="button" class="btn-secondary" data-action="close-new-budget-modal">Cancelar</button>
              <button type="submit" class="btn-primary" data-testid="submit-budget-btn">Guardar Presupuesto</button>
            </div>
          </form>
        </div>
      </div>

      <!-- Modal Nueva Meta -->
      <div id="new-goal-modal" class="modal-overlay" style="display: none;">
        <div class="modal-content">
          <div class="card-header">
            <h3 class="card-title">Definir Meta de Ahorro</h3>
            <button class="btn-secondary" style="padding: 2px 8px;" data-action="close-new-goal-modal">&times;</button>
          </div>
          <form id="new-goal-form" data-form="new-goal">
            <div style="margin-bottom: 12px;">
              <label for="goal-name" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Nombre de la Meta:</label>
              <input type="text" id="goal-name" data-testid="goal-name" required placeholder="Ej: Fondo de Emergencia, Vacaciones" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 12px;">
              <label for="goal-account" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Cuenta Asociada:</label>
              <select id="goal-account" data-testid="goal-account" required style="width: 100%;">
                ${assetAccounts.map((a) => `<option value="${escapeHtml(a.accountId)}">${escapeHtml(a.name)}</option>`).join("")}
              </select>
            </div>
            <div style="margin-bottom: 12px;">
              <label for="goal-target" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Importe Objetivo (${currency === "PEN" ? "S/" : currency === "USD" ? "$" : "€"}):</label>
              <input type="number" step="0.01" min="0.01" id="goal-target" data-testid="goal-target" required placeholder="Ej: 5000.00" style="width: 100%;" />
            </div>
            <div style="margin-bottom: 16px;">
              <label for="goal-date" style="font-size: 0.85rem; color: var(--text-secondary); display: block; margin-bottom: 4px;">Fecha Objetivo:</label>
              <input type="date" id="goal-date" data-testid="goal-date" required style="width: 100%;" value="${escapeHtml(new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10))}" />
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 8px;">
              <button type="button" class="btn-secondary" data-action="close-new-goal-modal">Cancelar</button>
              <button type="submit" class="btn-primary" data-testid="submit-goal-btn">Guardar Meta</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  `;
}
